import React, { FC, useEffect, useRef, useState } from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import {
  BooleanInput,
  CreateBase,
  EditBase,
  NumberInput,
  SimpleForm,
  TextInput,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Switch,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { Auth0RoleWithPermissionsOut, ChatModelOut, PortalSettingsOut } from '../../types/apiRequests'
import { getJson, getUrl, postJson, uploadBrandingFile } from '../../providers/data'
import { SaveOnlyToolbar } from '../../components'
import { themeColor } from '../../layout/themes'
import ImportExportSection from '../ImportExport/ImportExportSection'

type Section = 'branding' | 'tiers' | 'features' | 'defaults' | 'chat' | 'import_export'

const NAV_ITEMS: { key: Section; icon: React.ReactNode }[] = [
  { key: 'branding', icon: <PaletteOutlinedIcon /> },
  { key: 'tiers', icon: <TuneIcon /> },
  { key: 'features', icon: <ToggleOnOutlinedIcon /> },
  { key: 'defaults', icon: <AttachMoneyIcon /> },
  { key: 'chat', icon: <ChatBubbleOutlineIcon /> },
  { key: 'import_export', icon: <ImportExportIcon /> },
]

const DEFAULT_TIER_CONFIG = {
  free: { mcpSlots: 1, seatMax: 1 },
  team: { mcpSlots: 3, seatMax: 6 },
  enterprise: { mcpSlots: 999, seatMax: 999 },
}


const ColorSwatch: FC<{ source: string }> = ({ source }) => {
  const value = useWatch<Record<string, string | undefined>>({ name: source })
  const isValid = /^#[0-9a-fA-F]{6}$/.test(value || '')
  return (
    <Box
      sx={{
        mt: '16px',
        width: 40,
        height: 40,
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.15)',
        bgcolor: isValid ? value : 'transparent',
        transition: 'background-color 0.2s',
        flexShrink: 0,
      }}
    />
  )
}

const ColorField: FC<{ source: string; label: string; helperText: string }> = ({
  source,
  label,
  helperText,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
    <Box sx={{ flexGrow: 1 }}>
      <TextInput source={source} label={label} fullWidth helperText={helperText} />
    </Box>
    <ColorSwatch source={source} />
  </Box>
)

const FontPreview: FC = () => {
  const value = useWatch<{ font_family?: string }>({ name: 'font_family' }) || 'DM Sans'
  return (
    <Box sx={{ mt: -1, mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '8px' }}>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(value)}:wght@400;700&display=swap');`}
      </style>
      <Typography sx={{ fontFamily: `'${value}', sans-serif`, fontSize: '1rem' }}>
        The quick brown fox jumps over the lazy dog
      </Typography>
      <Typography
        sx={{ fontFamily: `'${value}', sans-serif`, fontSize: '0.8rem', color: 'text.secondary' }}
      >
        ABCDEFGHIJKLMNOPQRSTUVWXYZ · 0123456789
      </Typography>
    </Box>
  )
}

const RadiusPreview: FC = () => {
  const value = useWatch<{ border_radius?: number }>({ name: 'border_radius' }) ?? 10
  return (
    <Box sx={{ mt: -1, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
      <Box
        sx={{
          width: 60,
          height: 40,
          borderRadius: `${value}px`,
          border: '2px solid rgba(0,0,0,0.15)',
          transition: 'border-radius 0.2s',
        }}
      />
    </Box>
  )
}

const SectionTitle: FC<{ children: string; mt?: number }> = ({ children, mt = 3 }) => (
  <Typography
    variant="subtitle2"
    sx={{
      mt, mb: 1, fontWeight: 700, textTransform: 'uppercase',
      fontSize: '0.7rem', letterSpacing: '0.05em', color: 'text.secondary',
    }}
  >
    {children}
  </Typography>
)

const SectionDescription: FC<{ children: string }> = ({ children }) => (
  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>
    {children}
  </Typography>
)

const ImageUploadField: FC<{
  source: string
  field: 'logo' | 'favicon'
  label: string
  settingsId?: string
}> = ({ source, field, label, settingsId }) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const { setValue } = useFormContext()
  const currentValue = useWatch<Record<string, string | undefined>>({ name: source })
  const inputRef = useRef<HTMLInputElement>(null)

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !settingsId) return
    try {
      const result = await uploadBrandingFile(settingsId, field, file)
      if (result?.url) setValue(source, result.url, { shouldDirty: true })
      notify('Upload successful', { type: 'success' })
      refresh()
    } catch (error: unknown) {
      const err = error as { status?: number }
      if (err?.status === 401 || err?.status === 412) {
        window.location.reload()
        return
      }
      notify('Upload failed', { type: 'error' })
    }
    if (inputRef.current) inputRef.current.value = ''
  }

  const hasImage = !!(currentValue && currentValue.trim())

  return (
    <Box sx={{ mb: 2, width: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {label}
      </Typography>

      {hasImage && (
        <Box sx={{ mb: 1, p: 1, border: '1px solid rgba(0,0,0,0.12)', borderRadius: '8px', display: 'inline-block' }}>
          <Box
            component="img"
            src={currentValue}
            alt={field}
            sx={{ maxHeight: 48, maxWidth: 200, display: 'block' }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/x-icon,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => { void handleUpload(e) }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={() => inputRef.current?.click()}
          disabled={!settingsId}
          startIcon={<CloudUploadIcon />}
        >
          {hasImage ? translate('portalBranding.changeImage') : translate('portalBranding.uploadImage')}
        </Button>
      </Box>
    </Box>
  )
}


const BrandingSection: FC<{ settingsId?: string }> = ({ settingsId }) => {
  const translate = useTranslate()
  return (
    <>
      <SectionDescription>{translate('portalSettings.branding.description')}</SectionDescription>

      <SectionTitle mt={0}>
        {translate('portalBranding.section.colors')}
      </SectionTitle>
      <ColorField
        source="primary_color"
        label={translate('portalBranding.primaryColor')}
        helperText={translate('portalBranding.primaryColorHelp')}
      />
      <ColorField
        source="secondary_color"
        label={translate('portalBranding.secondaryColor')}
        helperText={translate('portalBranding.secondaryColorHelp')}
      />
      <ColorField
        source="login_background_color"
        label={translate('portalBranding.loginBgColor')}
        helperText={translate('portalBranding.loginBgColorHelp')}
      />
      <ColorField
        source="background_color"
        label={translate('portalBranding.bgColor')}
        helperText={translate('portalBranding.bgColorHelp')}
      />

      <SectionTitle>{translate('portalBranding.section.header')}</SectionTitle>
      <ColorField
        source="header_color"
        label={translate('portalBranding.headerColor')}
        helperText={translate('portalBranding.headerColorHelp')}
      />
      <ColorField
        source="header_text_color"
        label={translate('portalBranding.headerTextColor')}
        helperText={translate('portalBranding.headerTextColorHelp')}
      />

      <SectionTitle>{translate('portalBranding.section.typography')}</SectionTitle>
      <TextInput
        source="font_family"
        label="portalBranding.fontFamily"
        fullWidth
        helperText={translate('portalBranding.fontFamilyHelp')}
      />
      <FontPreview />
      <NumberInput
        source="border_radius"
        label="portalBranding.borderRadius"
        fullWidth
        helperText={translate('portalBranding.borderRadiusHelp')}
        min={0}
        max={20}
      />
      <RadiusPreview />

      <SectionTitle>{translate('portalBranding.section.secureAccessBranding')}</SectionTitle>
      <TextInput
        source="admin_title"
        label="portalBranding.adminTitle"
        fullWidth
        helperText={translate('portalBranding.adminTitleHelp')}
      />
      <TextInput
        source="login_subtitle"
        label="portalBranding.loginSubtitle"
        fullWidth
        helperText={translate('portalBranding.loginSubtitleHelp')}
      />

      <SectionTitle>{translate('portalBranding.section.mcpHubBranding')}</SectionTitle>
      <TextInput
        source="portal_title"
        label="portalBranding.portalTitle"
        fullWidth
        helperText={translate('portalBranding.portalTitleHelp')}
      />
      <ImageUploadField
        source="logo_url"
        field="logo"
        label={translate('portalBranding.logoUrl')}
        settingsId={settingsId}
      />
      <ImageUploadField
        source="favicon_url"
        field="favicon"
        label={translate('portalBranding.faviconUrl')}
        settingsId={settingsId}
      />

      <BooleanInput source="enabled" label="portalBranding.enabled" />
    </>
  )
}

const TiersSection: FC = () => {
  const translate = useTranslate()
  return (
    <>
      <SectionDescription>{translate('portalSettings.tiers.description')}</SectionDescription>

      <SectionTitle mt={0}>Free</SectionTitle>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <NumberInput
          source="tier_config.free.mcpSlots"
          label={translate('portalSettings.tiers.mcpSlots')}
          defaultValue={DEFAULT_TIER_CONFIG.free.mcpSlots}
          min={0}
        />
        <NumberInput
          source="tier_config.free.seatMax"
          label={translate('portalSettings.tiers.seatMax')}
          defaultValue={DEFAULT_TIER_CONFIG.free.seatMax}
          min={0}
        />
      </Box>

      <SectionTitle>Team</SectionTitle>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <NumberInput
          source="tier_config.team.mcpSlots"
          label={translate('portalSettings.tiers.mcpSlots')}
          defaultValue={DEFAULT_TIER_CONFIG.team.mcpSlots}
          min={0}
        />
        <NumberInput
          source="tier_config.team.seatMax"
          label={translate('portalSettings.tiers.seatMax')}
          defaultValue={DEFAULT_TIER_CONFIG.team.seatMax}
          min={0}
        />
      </Box>

      <SectionTitle>Enterprise</SectionTitle>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <NumberInput
          source="tier_config.enterprise.mcpSlots"
          label={translate('portalSettings.tiers.mcpSlots')}
          defaultValue={DEFAULT_TIER_CONFIG.enterprise.mcpSlots}
          min={0}
        />
        <NumberInput
          source="tier_config.enterprise.seatMax"
          label={translate('portalSettings.tiers.seatMax')}
          defaultValue={DEFAULT_TIER_CONFIG.enterprise.seatMax}
          min={0}
        />
      </Box>

      <SectionTitle>{translate('portalSettings.tiers.approval')}</SectionTitle>
      <BooleanInput
        source="free_tier_auto_approve"
        label={translate('portalSettings.tiers.autoApprove')}
        defaultValue={true}
      />
    </>
  )
}

const FeaturesSection: FC = () => {
  const translate = useTranslate()
  const { setValue } = useFormContext()
  const [roles, setRoles] = useState<Auth0RoleWithPermissionsOut[]>([])
  const [loadingRoles, setLoadingRoles] = useState(true)
  const currentRoleId = useWatch<{ beheerder_role_id?: string | null }>({ name: 'beheerder_role_id' })

  useEffect(() => {
    const controller = new AbortController()
    getJson<Auth0RoleWithPermissionsOut[]>(`${getUrl()}/auth0-roles`)
      .then((data) => { if (!controller.signal.aborted) setRoles(data) })
      .catch(() => { if (!controller.signal.aborted) setRoles([]) })
      .finally(() => { if (!controller.signal.aborted) setLoadingRoles(false) })
    return () => controller.abort()
  }, [])

  const selectedRole = roles.find((r) => r.id === currentRoleId) || null

  const handleRoleChange = (_: unknown, role: Auth0RoleWithPermissionsOut | null) => {
    setValue('beheerder_role_id', role?.id ?? '', { shouldDirty: true })
  }

  return (
    <>
      <SectionDescription>{translate('portalSettings.features.description')}</SectionDescription>

      <BooleanInput
        source="chat_enabled"
        label={translate('portalSettings.features.chatEnabled')}
        defaultValue={true}
      />

      <SectionTitle>{translate('portalSettings.features.authTitle')}</SectionTitle>
      <Autocomplete
        options={roles}
        value={selectedRole}
        onChange={handleRoleChange}
        getOptionLabel={(r) => r.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, r) => (
          <li {...props} key={r.id}>
            <Box>
              <Typography variant="body2">{r.name}</Typography>
              {r.description && (
                <Typography variant="caption" color="text.secondary">
                  {r.description}
                </Typography>
              )}
            </Box>
          </li>
        )}
        loading={loadingRoles}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            label={translate('portalSettings.features.beheerderRole')}
            helperText={translate('portalSettings.features.beheerderRoleHelp')}
            fullWidth
            margin="dense"
          />
        )}
        fullWidth
        sx={{ mb: 1 }}
      />
    </>
  )
}

// Platform-wide LiteLLM management key used by the spend importer to pull
// per-org cost every ~10 min. Write-only: an empty input is omitted from the
// save (key left untouched); the status chip reflects whether it is set.
const LiteLLMManagementKeyField: FC = () => {
  const translate = useTranslate()
  const record = useRecordContext<PortalSettingsOut>()
  const configured = !!record?.litellm_management_key_configured
  return (
    <>
      <SectionTitle>{translate('portalSettings.litellm.title')}</SectionTitle>
      <SectionDescription>{translate('portalSettings.litellm.description')}</SectionDescription>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          {translate('portalSettings.litellm.status')}
        </Typography>
        <Chip
          size="small"
          label={
            configured
              ? translate('portalSettings.litellm.configured')
              : translate('portalSettings.litellm.notConfigured')
          }
          color={configured ? 'success' : 'default'}
          variant={configured ? 'filled' : 'outlined'}
        />
      </Box>
      <TextInput
        source="litellm_management_api_key"
        label={translate('portalSettings.litellm.keyLabel')}
        helperText={translate('portalSettings.litellm.keyHelp')}
        type="password"
        fullWidth
        // The key is never returned in the record, so the field starts empty.
        // ``parse`` omits an empty input from the PUT so saving settings does
        // not wipe the stored key — only a typed value is sent.
        parse={(value: string) => value || undefined}
      />
    </>
  )
}

const DefaultsSection: FC = () => {
  const translate = useTranslate()
  return (
    <>
      <SectionDescription>{translate('portalSettings.defaults.description')}</SectionDescription>

      <NumberInput
        source="default_cost_per_seat"
        label={translate('portalSettings.defaults.costPerSeat')}
        fullWidth
        min={0}
        step={0.01}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />
      <NumberInput
        source="default_cost_per_mcp"
        label={translate('portalSettings.defaults.costPerMcp')}
        fullWidth
        min={0}
        step={0.01}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />
      <NumberInput
        source="default_cost_per_workflow"
        label={translate('portalSettings.defaults.costPerWorkflow')}
        fullWidth
        min={0}
        step={0.01}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />
      <NumberInput
        source="default_cost_per_workflow_execution"
        label={translate('portalSettings.defaults.costPerWorkflowExecution')}
        helperText={translate('portalSettings.defaults.costPerWorkflowExecutionHelp')}
        fullWidth
        min={0}
        step={0.0001}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />
      <NumberInput
        source="default_ai_quota_eur_per_hour"
        label={translate('portalSettings.defaults.aiQuotaEurPerHour')}
        helperText={translate('portalSettings.defaults.aiQuotaEurPerHourHelp')}
        fullWidth
        min={0}
        step={0.01}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />
      <NumberInput
        source="default_platform_budget"
        label={translate('portalSettings.defaults.platformBudget')}
        fullWidth
        min={0}
        step={0.01}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />
      <NumberInput
        source="default_workflow_budget"
        label={translate('portalSettings.defaults.workflowBudget')}
        fullWidth
        min={0}
        step={0.01}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />
      <NumberInput
        source="default_chat_budget"
        label={translate('portalSettings.defaults.chatBudget')}
        fullWidth
        min={0}
        step={0.01}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
      />

      <LiteLLMManagementKeyField />

      <SectionTitle>{translate('portalSettings.capacity.title')}</SectionTitle>
      <SectionDescription>{translate('portalSettings.capacity.description')}</SectionDescription>

      <NumberInput
        source="mcp_hour_ceiling"
        label={translate('portalSettings.capacity.mcpHourCeiling')}
        helperText={translate('portalSettings.capacity.mcpHourCeilingHelp')}
        fullWidth
        min={0}
        step={10000}
      />
      <NumberInput
        source="workflow_pods"
        label={translate('portalSettings.capacity.workflowPods')}
        helperText={translate('portalSettings.capacity.workflowPodsHelp')}
        fullWidth
        min={1}
        step={1}
      />
      <NumberInput
        source="avg_workflow_seconds"
        label={translate('portalSettings.capacity.avgWorkflowSeconds')}
        helperText={translate('portalSettings.capacity.avgWorkflowSecondsHelp')}
        fullWidth
        min={1}
        step={1}
      />
      <NumberInput
        source="capacity_alert_threshold_pct"
        label={translate('portalSettings.capacity.alertThresholdPct')}
        helperText={translate('portalSettings.capacity.alertThresholdPctHelp')}
        fullWidth
        min={1}
        max={100}
        step={1}
      />
    </>
  )
}


const ModelRow: FC<{
  model: ChatModelOut
  pending: boolean
  first: boolean
  onToggle: (next: boolean) => void
}> = ({ model, pending, first, onToggle }) => {
  const translate = useTranslate()
  return (
    <Box
      sx={{
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'space-between',
        'gap': 2,
        'px': 2,
        'py': 1.25,
        'borderTop': first ? 'none' : '1px solid',
        'borderColor': themeColor.slate10,
        'transition': 'background-color 0.12s ease',
        '&:hover': { backgroundColor: themeColor.slate05 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: model.enabled ? themeColor.black : themeColor.slate60,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {model.name}
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: themeColor.slate40, fontFamily: 'monospace' }}
        >
          {model.id}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            px: 1,
            py: 0.25,
            borderRadius: '6px',
            color: model.enabled ? themeColor.cyan : themeColor.slate40,
            backgroundColor: model.enabled ? themeColor.cyanMuted : themeColor.slate10,
          }}
        >
          {translate(model.enabled ? 'portalSettings.chat.enabled' : 'portalSettings.chat.disabled')}
        </Box>
        {pending ? (
          <CircularProgress size={18} sx={{ color: themeColor.slate40, mx: '6px' }} />
        ) : (
          <Switch
            checked={model.enabled}
            onChange={(e) => onToggle(e.target.checked)}
            size="small"
            sx={{
              '& .Mui-checked': { color: themeColor.cyan },
              '& .Mui-checked + .MuiSwitch-track': { backgroundColor: themeColor.cyan },
            }}
            inputProps={{ 'aria-label': model.name }}
          />
        )}
      </Box>
    </Box>
  )
}

const ChatSection: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const [models, setModels] = useState<ChatModelOut[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(false)
    getJson<ChatModelOut[]>(`${getUrl()}/org_openwebui_config/models`, {
      signal: controller.signal,
    })
      .then((data) => { if (!controller.signal.aborted) setModels(data) })
      .catch(() => { if (!controller.signal.aborted) setError(true) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [reloadKey])

  const handleToggle = async (model: ChatModelOut, next: boolean) => {
    if (pendingIds.has(model.id)) return
    setPendingIds((prev) => new Set(prev).add(model.id))
    setModels((prev) =>
      prev ? prev.map((m) => (m.id === model.id ? { ...m, enabled: next } : m)) : prev,
    )
    try {
      await postJson<ChatModelOut>(`${getUrl()}/org_openwebui_config/models/access`, {
        id: model.id,
        name: model.name,
        enabled: next,
      })
    } catch {
      // revert on failure
      setModels((prev) =>
        prev ? prev.map((m) => (m.id === model.id ? { ...m, enabled: !next } : m)) : prev,
      )
      notify(translate('portalSettings.chat.updateFailed'), { type: 'error' })
    } finally {
      setPendingIds((prev) => {
        const nextSet = new Set(prev)
        nextSet.delete(model.id)
        return nextSet
      })
    }
  }

  const enabledCount = models?.filter((m) => m.enabled).length ?? 0

  return (
    <>
      <SectionDescription>{translate('portalSettings.chat.description')}</SectionDescription>

      {loading ? (
        <Box sx={{ border: '1px solid', borderColor: themeColor.slate10, borderRadius: '10px' }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                px: 2, py: 1.5, borderTop: i === 0 ? 'none' : '1px solid', borderColor: themeColor.slate10,
              }}
            >
              <Box sx={{ width: '60%' }}>
                <Skeleton width="50%" height={18} />
                <Skeleton width="70%" height={12} />
              </Box>
              <Skeleton variant="rounded" width={34} height={20} />
            </Box>
          ))}
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              {translate('portalSettings.chat.retry')}
            </Button>
          }
        >
          {translate('portalSettings.chat.loadFailed')}
        </Alert>
      ) : !models || models.length === 0 ? (
        <Alert severity="info">{translate('portalSettings.chat.empty')}</Alert>
      ) : (
        <>
          <Typography sx={{ fontSize: 12, color: themeColor.slate40, mb: 1 }}>
            {translate('portalSettings.chat.enabledCount')
              .replace('{count}', String(enabledCount))
              .replace('{total}', String(models.length))}
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: themeColor.slate10, borderRadius: '10px', overflow: 'hidden' }}>
            {models.map((model, i) => (
              <ModelRow
                key={model.id}
                model={model}
                first={i === 0}
                pending={pendingIds.has(model.id)}
                onToggle={(next) => void handleToggle(model, next)}
              />
            ))}
          </Box>
        </>
      )}
    </>
  )
}

const FormContent: FC<{ section: Section; settingsId?: string }> = ({ section, settingsId }) => {
  switch (section) {
    case 'branding':
      return <BrandingSection settingsId={settingsId} />
    case 'tiers':
      return <TiersSection />
    case 'features':
      return <FeaturesSection />
    case 'defaults':
      return <DefaultsSection />
    case 'import_export':
      return null
    default:
      return null
  }
}

const PortalSettingsPage: FC = () => {
  const [section, setSection] = useState<Section>('branding')
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()

  const { data, isLoading } = useGetList<PortalSettingsOut>(
    'portal_settings',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created', order: 'DESC' },
    },
  )

  if (isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const existing = data?.[0]
  const newRecord = { enabled: true }

  const onSuccess = () => {
    notify(translate('portalSettings.saved'), { type: 'success' })
    refresh()
  }

  return (
    <Root>
      <Box className={classes.header}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {translate('portalSettings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('portalSettings.description')}
        </Typography>
      </Box>

      <Box className={classes.layout}>
        <nav className={classes.nav}>
          {NAV_ITEMS.map(({ key, icon }) => (
            <Box
              key={key}
              className={`${classes.navItem} ${section === key ? classes.navItemActive : ''}`}
              onClick={() => setSection(key)}
            >
              {icon}
              <span>{translate(`portalSettings.section.${navTranslationKey(key)}`)}</span>
            </Box>
          ))}
        </nav>

        <Box className={classes.content}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: themeColor.slate10 }}>
            <CardContent sx={{ p: 3 }}>
              {section === 'import_export' ? (
                <ImportExportSection />
              ) : section === 'chat' ? (
                <ChatSection />
              ) : existing ? (
                <EditBase
                  resource="portal_settings"
                  id={existing.id}
                  mutationMode="pessimistic"
                  mutationOptions={{ onSuccess }}
                >
                  <SimpleForm toolbar={<SaveOnlyToolbar />}>
                    <FormContent section={section} settingsId={String(existing.id)} />
                  </SimpleForm>
                </EditBase>
              ) : (
                <CreateBase
                  resource="portal_settings"
                  record={newRecord}
                  redirect={false}
                  mutationOptions={{ onSuccess }}
                >
                  <SimpleForm defaultValues={newRecord} toolbar={<SaveOnlyToolbar />}>
                    <FormContent section={section} />
                  </SimpleForm>
                </CreateBase>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Root>
  )
}

const navTranslationKey = (section: Section): string =>
  section === 'import_export' ? 'importExport' : section

const PREFIX = 'xxllncPortalSettings'

const classes = {
  header: `${PREFIX}-header`,
  layout: `${PREFIX}-layout`,
  nav: `${PREFIX}-nav`,
  navItem: `${PREFIX}-navItem`,
  navItemActive: `${PREFIX}-navItemActive`,
  content: `${PREFIX}-content`,
}

const Root = styled('div')(() => ({
  padding: '16px 24px',
  maxWidth: 1000,

  [`& .${classes.header}`]: {
    marginBottom: 24,
  },
  [`& .${classes.layout}`]: {
    display: 'flex',
    gap: 24,
    alignItems: 'flex-start',
  },
  [`& .${classes.nav}`]: {
    width: 200,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    position: 'sticky',
    top: 80,
  },
  [`& .${classes.navItem}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: themeColor.slate60,
    transition: 'all 0.15s ease',
    ['& .MuiSvgIcon-root']: {
      fontSize: 18,
    },
    ['&:hover']: {
      backgroundColor: themeColor.slate05,
      color: themeColor.black,
    },
  },
  [`& .${classes.navItemActive}`]: {
    backgroundColor: themeColor.blue10,
    color: themeColor.blue,
    fontWeight: 600,
    ['& .MuiSvgIcon-root']: {
      color: themeColor.blue,
    },
  },
  [`& .${classes.content}`]: {
    flex: 1,
    minWidth: 0,
  },
}))

export default PortalSettingsPage
